/**
 * The real connection to the Points Capture extension.
 *
 * The only file in the web app that touches `chrome.*`, kept to a dozen lines so
 * everything interesting about the bridge stays testable without a browser. No
 * `@types/chrome` here: a web page sees a tiny sliver of that API, and declaring
 * the sliver is honest about which parts exist.
 *
 * `window.chrome.runtime` is present on a page ONLY when an installed extension
 * declares this origin in its `externally_connectable.matches`, so its absence is
 * a real answer and not a feature test standing in for one. On a phone, in
 * another browser, or in a profile without the extension, it is simply not there.
 */

import { EXTENSION_ID, PORT_NAME } from "../../../shared/capturePort";
import type { PortTransport } from "./captureBridge";

interface RawPort {
  postMessage(message: unknown): void;
  disconnect(): void;
  onMessage: { addListener(fn: (message: unknown) => void): void };
  onDisconnect: { addListener(fn: () => void): void };
}

interface RawRuntime {
  connect?(extensionId: string, info: { name: string }): RawPort;
}

/** Open a port to the extension, or null when this browser cannot. */
export function connectToExtension(): PortTransport | null {
  const runtime = (globalThis as { chrome?: { runtime?: RawRuntime } }).chrome?.runtime;
  if (typeof runtime?.connect !== "function") return null;
  // Chrome does not report a missing extension here: the port comes back looking
  // open and then disconnects, which is why the bridge treats an early
  // disconnect as "absent" rather than as a lost connection.
  const port = runtime.connect(EXTENSION_ID, { name: PORT_NAME });
  return {
    postMessage: (message) => { port.postMessage(message); },
    // Throws if chrome has already closed it; the bridge calls this on teardown
    // and does not care which of the two happened first.
    disconnect: () => { port.disconnect(); },
    onMessage: (fn) => { port.onMessage.addListener(fn); },
    onDisconnect: (fn) => { port.onDisconnect.addListener(fn); },
  };
}
